import { SectionHeading } from "@/components/ui/section-heading";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Star, CheckCircle, Search, Filter, ChevronLeft, ChevronRight, Verified } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { motion, AnimatePresence } from "framer-motion";
import AutoScroll from "embla-carousel-auto-scroll";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import type { GuestReview } from "@shared/schema";
import { cn } from "@/lib/utils";
import { ReviewSubmissionForm } from "@/components/property/review-submission-form";
import { kualoaHeroPoster, portfolioHammock, portfolioShoreline } from "@/lib/media-assets";
import { KUALOA_PROPERTY_SLUG } from "@shared/property-context";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { useLocation } from "wouter";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";

interface ReviewSummary {
  averageRating: number;
  totalReviews: number;
  reviewCounts: { 1: number; 2: number; 3: number; 4: number; 5: number };
}

interface UnifiedReview {
  id: string;
  guest_name: string;
  guest_email?: string;
  location?: string;
  rating: number;
  review_date: Date;
  stay_start_date?: Date;
  stay_end_date?: Date;
  trip_type?: string;
  review_text?: string;
  would_recommend?: boolean;
  is_featured?: boolean;
  verified_guest?: boolean;
  reviewer_avatar_url?: string;
  source: "guest" | "airbnb";
  formattedDate: string;
  stayDuration?: number;
  trustScore: number;
  response_from_host?: string;
  response_date?: Date;
}

function ReviewsSection({ propertySlug }: { propertySlug?: string } = {}) {
  const scope = propertySlug ? { property: propertySlug } : undefined;
  // On the portfolio homepage the band speaks for every home, with its own photo.
  const flags = useFeatureFlags();
  const [pathname] = useLocation();
  const forAllHomes = flags.portfolioHomepage && flags.publishedHomeCount > 1 && !propertySlug && (pathname === "/" || pathname === "");
  // Bright, sunny photos only: the band sits behind white text under a green
  // wash, and the overcast beach shot it used to carry read as gloomy. The
  // Kualoa house shows itself under the cliffs; any other home gets the
  // Ka'a'awa shoreline rather than a photo of the Kualoa house.
  const bandImage = forAllHomes
    ? portfolioHammock
    : !propertySlug || propertySlug === KUALOA_PROPERTY_SLUG
      ? kualoaHeroPoster
      : portfolioShoreline;
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [filterRating, setFilterRating] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [emblaApi, setEmblaApi] = useState<CarouselApi>();
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isAllReviewsOpen, setIsAllReviewsOpen] = useState(false);
  const reviewsPerPage = 6;
  
  // Reset filters when All Reviews dialog closes
  const handleAllReviewsOpenChange = (open: boolean) => {
    setIsAllReviewsOpen(open);
    if (!open) {
      // Reset all filter states when dialog closes
      setSearchQuery("");
      setSortBy("recent");
      setFilterRating("all");
      setCurrentPage(1);
    }
  };
  
  const autoScrollPlugin = useRef(AutoScroll({ 
    speed: 0.5,
    startDelay: 1000,
    stopOnInteraction: false, 
    stopOnMouseEnter: true,
    playOnInit: true 
  }));
  
  const wheelGesturesPlugin = useRef(WheelGesturesPlugin({
    forceWheelAxis: "x"
  }));

  // Fetch guest reviews (all reviews are stored in database)
  const { data: guestReviews, isLoading: guestReviewsLoading, error: guestReviewsError } = useQuery<GuestReview[]>({
    queryKey: scope ? ["/api/reviews/public", scope] : ["/api/reviews/public"],
    retry: 3,
    staleTime: 5 * 60 * 1000
  });

  const { data: summary, isLoading: summaryLoading } = useQuery<ReviewSummary>({
    queryKey: scope ? ["/api/reviews/summary", scope] : ["/api/reviews/summary"],
    retry: 3,
    staleTime: 5 * 60 * 1000
  });

  // Calculate trust score for reviews
  const calculateTrustScore = useCallback((factors: {
    verified: boolean;
    hasReviewText: boolean;
    longStay: boolean;
    highRating: boolean;
    wouldRecommend: boolean;
    isAirbnb?: boolean;
  }): number => {
    let score = 0;
    if (factors.verified) score += 30;
    if (factors.hasReviewText) score += 25;
    if (factors.longStay) score += 15;
    if (factors.highRating) score += 20;
    if (factors.wouldRecommend) score += 10;
    if (factors.isAirbnb) score += 10;
    return score;
  }, []);

  // Format guest reviews
  const reviews = useMemo<UnifiedReview[]>(() => {
    const allReviews: UnifiedReview[] = [];

    // Process guest reviews (all reviews are stored in database)
    if (guestReviews) {
      guestReviews.forEach(review => {
        const stayDuration = review.stay_start_date && review.stay_end_date 
          ? Math.ceil((new Date(review.stay_end_date).getTime() - new Date(review.stay_start_date).getTime()) / (1000 * 60 * 60 * 24))
          : undefined;

        allReviews.push({
          id: review.id,
          guest_name: review.guest_name,
          location: review.location || undefined,
          rating: review.rating,
          review_date: new Date(review.review_date),
          stay_start_date: review.stay_start_date ? new Date(review.stay_start_date) : undefined,
          stay_end_date: review.stay_end_date ? new Date(review.stay_end_date) : undefined,
          trip_type: review.trip_type || undefined,
          review_text: review.review_text || undefined,
          would_recommend: review.would_recommend ?? undefined,
          is_featured: review.is_featured ?? undefined,
          verified_guest: review.verified_guest ?? undefined,
          source: "guest",
          formattedDate: new Date(review.review_date).toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric'
          }),
          stayDuration,
          trustScore: calculateTrustScore({
            verified: review.verified_guest || false,
            hasReviewText: !!review.review_text,
            longStay: stayDuration ? stayDuration >= 3 : false,
            highRating: review.rating >= 5,
            wouldRecommend: review.would_recommend || false
          }),
          response_from_host: (review as any).response_from_host || undefined,
          response_date: (review as any).response_date ? new Date((review as any).response_date) : undefined
        });
      });
    }

    return allReviews.sort((a, b) => {
      if (a.is_featured !== b.is_featured) {
        return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      }
      if (a.trustScore !== b.trustScore) {
        return b.trustScore - a.trustScore;
      }
      return b.review_date.getTime() - a.review_date.getTime();
    });
  }, [guestReviews, calculateTrustScore]);

  // Review highlights
  const reviewHighlights = [
    "Breathtaking ocean views and private beach access",
    "Perfect for families with children - safe and entertaining", 
    "Amazing hosts with exceptional communication",
    "Fantastic water activities - kayaks and SUPs included",
    "Unhurried days and quiet nights, with only the sound of the water",
    "Right beside Kualoa Beach Park, with miles of pristine shoreline to yourselves"
  ];

  // Featured reviews for carousel - exactly the 9 curated featured reviews
  const featuredReviews = useMemo(() => {
    return reviews
      .filter(review => review.is_featured)
      .slice(0, 9);
  }, [reviews]);

  const isLoading = guestReviewsLoading || summaryLoading;
  const hasError = guestReviewsError;

  // Fallback data
  const fallbackSummary: ReviewSummary = {
    averageRating: 4.8,
    totalReviews: 0,
    reviewCounts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  };

  // Filter and sort reviews for lightbox modal
  const filteredAndSortedReviews = useMemo(() => {
    if (!reviews) return [];
    
    return reviews.filter(review => {
      const matchesSearch = searchQuery === "" || 
        review.guest_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.review_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRating = filterRating === "all" || review.rating.toString() === filterRating;
      
      return matchesSearch && matchesRating;
    }).sort((a, b) => {
      switch (sortBy) {
        case "recent":
          return new Date(b.review_date).getTime() - new Date(a.review_date).getTime();
        case "oldest":
          return new Date(a.review_date).getTime() - new Date(b.review_date).getTime();
        case "highest":
          return b.rating - a.rating;
        case "lowest":
          return a.rating - b.rating;
        default:
          return 0;
      }
    });
  }, [reviews, searchQuery, filterRating, sortBy]);

  // Pagination for lightbox
  const totalPages = Math.ceil(filteredAndSortedReviews.length / reviewsPerPage);
  const paginatedReviews = filteredAndSortedReviews.slice(
    (currentPage - 1) * reviewsPerPage,
    currentPage * reviewsPerPage
  );

  // Lightbox navigation functions
  const handlePreviousReview = useCallback(() => {
    setSelectedReviewIndex(prev => 
      prev > 0 ? prev - 1 : filteredAndSortedReviews.length - 1
    );
  }, [filteredAndSortedReviews.length]);

  const handleNextReview = useCallback(() => {
    setSelectedReviewIndex(prev => 
      prev < filteredAndSortedReviews.length - 1 ? prev + 1 : 0
    );
  }, [filteredAndSortedReviews.length]);

  // Handle review click to open lightbox
  const handleReviewClick = useCallback((reviewId: string) => {
    const index = filteredAndSortedReviews.findIndex(review => review.id === reviewId);
    if (index !== -1) {
      setSelectedReviewIndex(index);
      setIsLightboxOpen(true);
    }
  }, [filteredAndSortedReviews]);

  // Reset search when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortBy, filterRating]);

  // Render star rating
  const renderStars = (rating: number, size: "sm" | "md" | "lg" = "md") => {
    const sizeClasses = {
      sm: "h-3 w-3",
      md: "h-4 w-4", 
      lg: "h-5 w-5"
    };

    return Array.from({ length: 5 }, (_, i) => {
      const isFilled = i < Math.floor(rating);
      
      return (
        <Star 
          key={i} 
          fill={isFilled ? "currentColor" : "none"}
          className={`${sizeClasses[size]} transition-all duration-300 ${
            isFilled 
              ? 'text-sun drop-shadow-none' 
              : 'text-sand-deep stroke-current fill-none'
          }`} 
        />
      );
    });
  };

  // Render trust badges
  const renderTrustBadge = (review: UnifiedReview) => {
    if (review.trustScore < 70) return null;

    return (
      <div className="flex items-center space-x-1">
        {review.verified_guest && (
          <Badge 
            variant="secondary" 
            className="bg-sand text-ink text-[11px] font-sans font-semibold tracking-[0.3em] uppercase px-2 py-1 border-none"
            data-testid={`verified-badge-${review.id}`}
          >
            Verified Stay
          </Badge>
        )}
      </div>
    );
  };

  // Render single review card. expanded=true (lightbox) shows full text with no clamp.
  const renderReviewCard = (
    review: UnifiedReview,
    index: number,
    isClickable: boolean = true,
    expanded: boolean = false,
  ) => (
    <Card 
      className={cn(
        "shadow-none border border-sand-deep bg-shell transition-all duration-300 ease-out group",
        expanded ? "h-auto" : "h-80 hover:border-sand-deep",
        isClickable && "cursor-pointer hover:bg-sand"
      )}
      onClick={isClickable ? () => handleReviewClick(review.id) : undefined}
      data-testid={`review-card-${review.id}`}
    >
      <CardContent className={cn(
        "pt-8 pb-8 px-8 relative flex flex-col",
        expanded ? "h-auto overflow-visible" : "overflow-hidden h-full",
      )}>
        {/* Header */}
        <div className="mb-4 flex-shrink-0">
          <div className="flex items-center space-x-2 mb-1">
            <h4 className={cn(
              "text-luxury-lg font-serif font-normal text-forest tracking-tight",
              !expanded && "truncate",
            )}>
              {review.guest_name}
            </h4>
            {renderTrustBadge(review)}
          </div>
          {review.location && (
            <p className={cn(
              "text-luxury-sm font-sans font-light text-ink-soft tracking-wide",
              !expanded && "truncate",
            )}>
              {review.location}
            </p>
          )}
        </div>

        {/* Rating */}
        <div className="flex items-center space-x-3 mb-4 flex-shrink-0">
          <div className="flex gap-0.5">{renderStars(review.rating, "sm")}</div>
          <span className="text-luxury-sm font-sans font-light text-ink-soft tracking-wide">
            {review.formattedDate}
          </span>
        </div>

        {/* Review Text */}
        {review.review_text && (
          <div className={cn("mb-4", expanded ? "flex-none" : "flex-1 overflow-hidden")}>
            <p className={cn(
              "text-luxury-base font-sans text-ink-soft leading-relaxed font-light whitespace-pre-wrap",
              !expanded && "line-clamp-4",
            )}>
              "{review.review_text}"
            </p>
          </div>
        )}

        {/* Host Response */}
        {review.response_from_host && (
          <div className="mt-2 mb-4 p-4 bg-sand rounded-sm border-l border-sand-deep">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[11px] font-sans font-semibold text-driftwood uppercase tracking-[0.3em]">
                Response from Host
              </span>
            </div>
            <p className={cn(
              "text-luxury-sm font-sans font-light text-ink-soft leading-relaxed whitespace-pre-wrap",
              !expanded && "line-clamp-3",
            )}>
              {review.response_from_host}
            </p>
          </div>
        )}

        {/* Trip details */}
        <div className="flex items-center justify-between text-luxury-xs text-ink-soft flex-shrink-0 mt-auto">
          {review.trip_type && (
            <span className="text-luxury-xs font-sans font-light tracking-wide uppercase text-driftwood">
              {review.trip_type}
            </span>
          )}
          {review.stayDuration && (
            <span className="text-luxury-xs font-sans font-light tracking-wide">
              {review.stayDuration} night{review.stayDuration !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <section 
        className="py-16 md:py-24 bg-sand"
        aria-label="Guest reviews"
        role="region"
        aria-busy="true"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-8 w-64 mb-8" />
            <div className="flex space-x-4 mb-8 overflow-hidden">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i} className="flex-shrink-0 w-80 shadow-none border border-sand-deep">
                  <CardContent className="pt-8 pb-8 px-8">
                    <div className="mb-6">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                    <Skeleton className="h-3 w-full mb-3" />
                    <Skeleton className="h-3 w-full mb-3" />
                    <Skeleton className="h-3 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (hasError && guestReviewsError) {
    console.warn('Reviews API error:', { guestReviewsError });
  }

  const summaryData = summary || fallbackSummary;
  const reviewsData = reviews || [];

  if ((!reviewsData || reviewsData.length === 0) && !isLoading) {
    return (
      <section 
        className="py-20 md:py-24 bg-sand"
        aria-labelledby="reviews-heading-empty"
        role="region"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <SectionHeading
              align="center"
              eyebrow="Reviews"
              title="Guest Reviews"
              className="mb-8"
              id="reviews-heading-empty"
            />
            <div className="py-16 text-ink-soft">
              <Star className="h-16 w-16 mx-auto mb-4 opacity-30 text-ink-soft" />
              <p className="text-luxury-xl font-serif mb-2 text-ink">No reviews yet</p>
              <p className="mb-8 font-light">Be the first to share your experience!</p>
              <ReviewSubmissionForm />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="reviews"
      className="pt-20 md:pt-24 bg-sand section-stable border-t border-sand-deep"
      aria-labelledby="reviews-heading"
      role="region"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header: title + score clustered with star distribution (no full-width stretch) */}
          <ScrollReveal>
            <header className="mb-10 md:mb-12">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] lg:gap-14 items-center">
                {/* Left: what this section is, in a sentence */}
                <div>
                  <p className="text-[11px] font-sans font-semibold text-driftwood uppercase tracking-[0.3em] mb-3">
                    Reviews
                  </p>
                  <h2
                    id="reviews-heading"
                    className="text-luxury-4xl font-serif font-normal text-ink mb-4 leading-tight tracking-luxury-tight"
                  >
                    Guest Reviews
                  </h2>
                  <p className="text-ink-soft text-luxury-lg font-light leading-relaxed max-w-md">
                    Every review below comes from a completed stay. Nothing is edited, nothing is bought.
                  </p>
                  <dl className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                    <div className="border border-sand-deep bg-shell px-4 py-3">
                      <dt className="text-[10px] font-sans uppercase tracking-[0.2em] text-driftwood">Average</dt>
                      <dd className="mt-1 font-serif text-[22px] leading-none text-ink">{summaryData.averageRating}</dd>
                    </div>
                    <div className="border border-sand-deep bg-shell px-4 py-3">
                      <dt className="text-[10px] font-sans uppercase tracking-[0.2em] text-driftwood">Five-star</dt>
                      <dd className="mt-1 font-serif text-[22px] leading-none text-ink">
                        {summaryData.totalReviews > 0 ? Math.round((summaryData.reviewCounts[5] / summaryData.totalReviews) * 100) : 0}%
                      </dd>
                    </div>
                    <div className="border border-sand-deep bg-shell px-4 py-3">
                      <dt className="text-[10px] font-sans uppercase tracking-[0.2em] text-driftwood">Stays</dt>
                      <dd className="mt-1 font-serif text-[22px] leading-none text-ink" data-testid="total-reviews">{summaryData.totalReviews}</dd>
                    </div>
                  </dl>
                </div>

                {/* Right: the score card, distribution inside it, nothing floating */}
                <div className="border border-sand-deep bg-shell p-6 md:p-7">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-serif text-[44px] leading-none text-ink tracking-tight" data-testid="average-rating">
                          {summaryData.averageRating}
                        </span>
                        <span className="text-ink-soft font-sans text-[13px]">out of 5</span>
                      </div>
                      <div className="mt-2 flex items-center gap-1" aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} className="h-4 w-4 text-sun fill-current" />
                        ))}
                      </div>
                      <p className="mt-2 text-ink-soft font-sans text-[13px]">{summaryData.totalReviews} reviews from verified stays</p>
                    </div>
                    <Badge
                      variant="secondary"
                      className="bg-forest/10 border-0 text-forest text-[10px] font-sans font-semibold uppercase tracking-[0.25em] px-2.5 py-1 shrink-0"
                    >
                      Superhost
                    </Badge>
                  </div>

                  <div className="mt-5 pt-5 border-t border-sand-deep space-y-2.5" aria-label="Rating distribution">
                    {[5, 4, 3, 2, 1].map((stars) => {
                      const count = summaryData.reviewCounts[stars as keyof typeof summaryData.reviewCounts];
                      const percentage = summaryData.totalReviews > 0 ? Math.round((count / summaryData.totalReviews) * 100) : 0;
                      return (
                        <motion.div
                          key={stars}
                          className="flex items-center gap-3"
                          initial={{ opacity: 0, x: 12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: (5 - stars) * 0.08, duration: 0.3 }}
                        >
                          <span className="text-[13px] text-ink-soft w-3 text-right font-light tabular-nums">{stars}</span>
                          <Star className="h-3 w-3 text-sun fill-current shrink-0" aria-hidden="true" />
                          <div
                            className="flex-1 h-1.5 bg-sand border border-sand-deep/40 rounded-full overflow-hidden"
                            role="progressbar"
                            aria-label={`${stars} star reviews: ${percentage}%`}
                            aria-valuenow={percentage}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <motion.div
                              className="h-full bg-forest rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ delay: (5 - stars) * 0.08 + 0.25, duration: 0.55, ease: "easeOut" }}
                            />
                          </div>
                          <span className="text-[13px] text-ink-soft font-light w-9 text-right tabular-nums">{percentage}%</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </header>
          </ScrollReveal>

          {/* Enhanced Continuous Scroll Carousel - MOVED BEFORE "What guests love" */}
          <div className="relative mb-12 overflow-visible py-6">
            <ScrollReveal>
              <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between mb-8 mt-12 border-t border-sand-deep pt-12">
                <h3 className="text-luxury-2xl font-serif font-normal text-forest tracking-tight">Featured Reviews</h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <ReviewSubmissionForm />
                  <Dialog open={isAllReviewsOpen} onOpenChange={handleAllReviewsOpenChange}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" data-testid="see-all-reviews-button" className="border-sand-deep text-ink hover:bg-sand hover:text-ink font-sans tracking-wide h-auto py-3 px-6">
                        See All Reviews ({reviewsData.length})
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden bg-shell border-sand-deep p-0 rounded-sm">
                    <DialogHeader className="pt-8 px-8 pb-4 border-b border-sand-deep bg-sand">
                      <DialogTitle className="text-luxury-2xl font-serif font-normal text-forest">All Reviews ({filteredAndSortedReviews.length})</DialogTitle>
                    </DialogHeader>
                    
                    {/* Search and Filter Controls */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-6 px-8 pt-6">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-ink-soft" />
                        <Input
                          placeholder="Search reviews..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 border-sand-deep bg-shell focus-visible:ring-ink"
                          data-testid="search-reviews-input"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Select value={sortBy} onValueChange={setSortBy}>
                          <SelectTrigger className="w-32 border-sand-deep bg-shell focus:ring-ink">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-shell border-sand-deep">
                            <SelectItem value="recent">Most Recent</SelectItem>
                            <SelectItem value="oldest">Oldest First</SelectItem>
                            <SelectItem value="highest">Highest Rated</SelectItem>
                            <SelectItem value="lowest">Lowest Rated</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={filterRating} onValueChange={setFilterRating}>
                          <SelectTrigger className="w-24">
                            <Filter className="h-4 w-4" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="5">5 Stars</SelectItem>
                            <SelectItem value="4">4 Stars</SelectItem>
                            <SelectItem value="3">3 Stars</SelectItem>
                            <SelectItem value="2">2 Stars</SelectItem>
                            <SelectItem value="1">1 Star</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Reviews Grid */}
                    <div className="grid md:grid-cols-2 gap-4 max-h-[50vh] overflow-y-auto px-8 pb-8">
                      {paginatedReviews.map((review, index) => (
                        <div key={review.id}>
                          {renderReviewCard(review, index, false)}
                        </div>
                      ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center space-x-2 pb-6 pt-2 border-t border-sand-deep bg-shell">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="border-sand-deep text-ink hover:bg-sand"
                        >
                          Previous
                        </Button>
                        <span className="text-luxury-sm text-ink-soft">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="border-sand-deep text-ink hover:bg-sand"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                </div>
              </div>
            </ScrollReveal>

            {/* Continuous Scrolling Carousel without arrows */}
            <Carousel
              className="w-full"
              plugins={[autoScrollPlugin.current, wheelGesturesPlugin.current]}
              setApi={setEmblaApi}
              opts={{
                align: "start",
                loop: true,
                dragFree: true,
                containScroll: false,
                skipSnaps: true,
                duration: 15
              }}
            >
              <CarouselContent className="-ml-2 md:-ml-4 py-4">
                {featuredReviews.map((review, index) => (
                  <CarouselItem 
                    key={review.id} 
                    className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 min-w-0"
                    style={{ flex: '0 0 auto', minWidth: '280px', maxWidth: '360px' }}
                    data-testid={`carousel-slide-${index}`}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                    >
                      {renderReviewCard(review, index)}
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>

        </div>
      </div>

      {/* Full-bleed photo band: breaks up the sand sections with place imagery */}
      <div
        className="relative mt-12 md:mt-16 overflow-hidden text-white"
        role="region"
        aria-labelledby="guests-love-heading"
      >
        <div className="absolute inset-0 z-0" aria-hidden="true">
          <img
            src={optimizedUrl(bandImage, 1600)}
            srcSet={optimizedSrcSet(bandImage, [768, 1200, 1600, 2000])}
            sizes="100vw"
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-[center_40%]"
            loading="lazy"
            decoding="async"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(23,49,28,0.28) 0%, rgba(23,49,28,0.40) 48%, rgba(23,49,28,0.62) 100%)",
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-5 md:px-8 max-w-[1040px] py-14 sm:py-16 md:py-[88px]">
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10 md:mb-14">
              <p className="wordmark-gold text-[11px] md:text-xs font-sans font-normal uppercase tracking-[0.3em] mb-3">
                From our guests
              </p>
              <h3
                id="guests-love-heading"
                className="wordmark-gold font-serif font-[500] text-[clamp(26px,5.2vw,44px)] leading-[1.15] tracking-tight"
              >
                {forAllHomes ? "What guests love about these homes" : "What guests love about this place"}
              </h3>
              <div className="flex items-center gap-3 mt-[18px] w-full max-w-[min(80vw,360px)] mx-auto">
                <span className="h-px flex-1 bg-[#E3CF98]/55" />
                <span className="w-[7px] h-[7px] rotate-45 bg-[#E3CF98]" />
                <span className="h-px flex-1 bg-[#E3CF98]/55" />
              </div>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 list-none p-0 m-0">
              {reviewHighlights.map((highlight, index) => (
                <motion.li
                  key={highlight}
                  className="flex items-start gap-3 md:gap-4 px-4 py-4 md:px-5 md:py-5 bg-white/[0.1] backdrop-blur-md border border-white/25"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ delay: Math.min(index * 0.06, 0.3), duration: 0.35 }}
                >
                  <CheckCircle className="h-5 w-5 text-[#E3CF98] mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <span className="text-[15px] md:text-base text-white leading-relaxed font-sans font-normal">
                    {highlight}
                  </span>
                </motion.li>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </div>

      {/* Lightbox Modal for individual review viewing */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col bg-shell border border-sand-deep p-0 rounded-sm">
          <DialogHeader className="bg-sand pt-8 px-8 pb-6 border-b border-sand-deep shrink-0">
            <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
              <span className="text-luxury-2xl font-serif font-normal text-ink">Review Details</span>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousReview}
                  disabled={filteredAndSortedReviews.length <= 1}
                  data-testid="previous-review-button"
                  className="border-sand-deep text-ink hover:bg-shell"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-luxury-sm text-ink-soft px-2">
                  {selectedReviewIndex + 1} of {filteredAndSortedReviews.length}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextReview}
                  disabled={filteredAndSortedReviews.length <= 1}
                  data-testid="next-review-button"
                  className="border-sand-deep text-ink hover:bg-shell"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {filteredAndSortedReviews[selectedReviewIndex] && (
            <div className="p-8 overflow-y-auto flex-1 min-h-0">
              {renderReviewCard(
                filteredAndSortedReviews[selectedReviewIndex],
                selectedReviewIndex,
                false,
                true,
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default ReviewsSection;